import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, getToken, setToken } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  // On load, if we have a token, confirm it's still valid.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!getToken()) {
        setReady(true);
        return;
      }
      try {
        const { user } = await apiGet("/auth/me");
        if (!cancelled) setUser(user);
      } catch {
        setToken("");
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Returns { pending, devCode } — caller routes to the verify screen.
  const register = useCallback(async ({ email, password, displayName, memberCode }) => {
    return apiPost("/auth/register", { email, password, displayName, memberCode });
  }, []);

  const verify = useCallback(async ({ email, code }) => {
    const { token, user } = await apiPost("/auth/verify", { email, code });
    setToken(token);
    setUser(user);
    return user;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { token, user } = await apiPost("/auth/login", { email, password });
    setToken(token);
    setUser(user);
    return user;
  }, []);

  const resend = useCallback(async (email) => {
    return apiPost("/auth/resend", { email });
  }, []);

  const resetPassword = useCallback(async ({ email, token, password }) => {
    const { token: authToken, user } = await apiPost("/auth/reset-password", {
      email,
      token,
      password,
    });
    setToken(authToken);
    setUser(user);
    return user;
  }, []);

  // Admin-invited members set a password instead of registering; the token from
  // the emailed link stands in for the member code and email verification.
  const acceptInvite = useCallback(async ({ email, token, password }) => {
    const { token: authToken, user } = await apiPost("/auth/accept-invite", {
      email,
      token,
      password,
    });
    setToken(authToken);
    setUser(user);
    return user;
  }, []);

  const logout = useCallback(() => {
    setToken("");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, ready, register, verify, login, resend, acceptInvite, resetPassword, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
